import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Loader2, Download, Play } from 'lucide-react';
import { parseKeyboardEvent } from '@/hooks/useMicrotonalNostr';
import { useMicrotonalLibrary } from '@/hooks/useMicrotonalLibrary';
import { useNostr } from '@nostrify/react';
import { PlayableKeyboard } from '@/components/microtonal/PlayableKeyboard';
import { useToast } from '@/hooks/useToast';
import { frequencyForNote, degreesPerPeriod } from '@/lib/microtonal/scale';
import { useAuthor } from '@/hooks/useAuthor';
import type { Keyboard } from '@/lib/microtonal/types';

interface KeyboardViewerProps {
  pubkey: string;
  dTag: string;
  kind: number;
}

export function KeyboardViewer({ pubkey, dTag, kind }: KeyboardViewerProps) {
  const { nostr } = useNostr();
  const { importKeyboard } = useMicrotonalLibrary();
  const { toast } = useToast();
  const author = useAuthor(pubkey);

  const { data, isLoading } = useQuery({
    queryKey: ['nostr', 'keyboard', pubkey, dTag],
    queryFn: async (c) => {
      const [event] = await nostr.query(
        [{ kinds: [kind], authors: [pubkey], '#d': [dTag], limit: 1 }],
        { signal: c.signal }
      );
      if (!event) throw new Error('Keyboard not found');
      const keyboard = parseKeyboardEvent(event);
      if (!keyboard) throw new Error('Invalid keyboard data');
      return { event, keyboard };
    },
    staleTime: 300_000,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="size-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Keyboard not found or invalid data.</p>
      </div>
    );
  }

  const { keyboard } = data;
  const authorName = author.data?.metadata?.name ?? pubkey.slice(0, 16);
  const dpp = degreesPerPeriod(keyboard.scale);

  const handleImport = () => {
    const imported = importKeyboard(keyboard);
    toast({ title: 'Imported!', description: `"${imported.name}" added to your library` });
  };

  return (
    <div className="container mx-auto max-w-4xl py-8 space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-2xl">{keyboard.name}</CardTitle>
              <CardDescription className="mt-1">
                Shared by {authorName} · {dpp} degrees per {keyboard.scale.periodCents}c period
              </CardDescription>
            </div>
            <Button onClick={handleImport}>
              <Download className="size-4 mr-2" /> Import
            </Button>
          </div>
        </CardHeader>
        {keyboard.description && (
          <CardContent>
            <p className="text-muted-foreground">{keyboard.description}</p>
          </CardContent>
        )}
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg"><Play className="size-5" /> Try It</CardTitle>
          <CardDescription>Click keys or use your QWERTY keyboard to play this microtonal keyboard.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto pb-2">
            <PlayableKeyboard keyboard={keyboard} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Scale Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-muted-foreground">Period:</div>
            <div>{keyboard.scale.periodCents} cents</div>
            <div className="text-muted-foreground">Degrees per period:</div>
            <div>{dpp}</div>
            <div className="text-muted-foreground">Base frequency:</div>
            <div>{keyboard.scale.baseFrequency} Hz</div>
            <div className="text-muted-foreground">Total keys:</div>
            <div>{keyboard.keys.length}</div>
          </div>
          <div className="pt-2">
            <div className="text-sm font-medium mb-2">Scale steps:</div>
            <div className="flex flex-wrap gap-1.5">
              <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-700 dark:text-amber-400">0c (root)</span>
              {keyboard.scale.steps.map((step, i) => (
                <span key={i} className="text-xs px-2 py-1 rounded bg-muted">
                  {step.cents}c{step.label ? ` (${step.label})` : ''}
                </span>
              ))}
              <span className="text-xs px-2 py-1 rounded bg-indigo-500/20 text-indigo-700 dark:text-indigo-400">
                {keyboard.scale.periodCents}c (period)
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
